import { Crosshair, User, Users } from 'lucide-react';
import { HTMLAttributes } from 'react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface VariantTargetingAudienceProps extends HTMLAttributes<HTMLDivElement> {
  isEnabled: boolean;
  isTargetedRollout: boolean;
  isPercentageRollout: boolean;
}

type TargetingSubtypeProps = Omit<
  VariantTargetingAudienceProps,
  'isTargetedRollout' | 'isPercentageRollout' | 'isEnabled'
>;

function TargetingAudience({
  className,
  children,
  ...rest
}: TargetingSubtypeProps) {
  return (
    <Badge
      {...rest}
      variant="outline"
      className={cn(
        'flex items-center gap-1.5 text-muted-foreground w-fit whitespace-nowrap',
        className,
      )}
    >
      {children}
    </Badge>
  );
}

export const VariantTargetingAudience = ({
  isEnabled,
  isTargetedRollout,
  isPercentageRollout,
  className,
  ...rest
}: VariantTargetingAudienceProps) => {
  if (!isEnabled) {
    return (
      <TargetingAudience className={className} {...rest}>
        <Crosshair className="w-4 h-4" />
        <div>Default variant</div>
      </TargetingAudience>
    );
  }

  if (isTargetedRollout || isPercentageRollout) {
    const nodes = [];
    if (isTargetedRollout) {
      nodes.push(
        <TargetingAudience key="targeted" className={className} {...rest}>
          <User className="w-4 h-4" />
          <div>Targeting rules</div>
        </TargetingAudience>,
      );
    }
    if (isPercentageRollout) {
      nodes.push(
        <TargetingAudience key="percentage" className={className} {...rest}>
          <Users className="w-4 h-4" />
          <div>Percentage rollout</div>
        </TargetingAudience>,
      );
    } else {
      nodes.push(
        <TargetingAudience key="default" className={className} {...rest}>
          <Crosshair className="w-4 h-4" />
          <div>Default variant</div>
        </TargetingAudience>,
      );
    }

    return nodes;
  }

  return (
    <TargetingAudience className={className} {...rest}>
      <Crosshair className="w-4 h-4" />
      <div>Default variant</div>
    </TargetingAudience>
  );
};

export const getTargetingAudienceBadgesForSidebar = ({
  isEnabled,
  isTargetedRollout,
  isPercentageRollout,
  className,
  ...rest
}: VariantTargetingAudienceProps) => {
  if (!isEnabled) {
    return [
      <TargetingAudience key="default" className={className} {...rest}>
        <Crosshair className="w-4 h-4" />
        <div>Default variant</div>
      </TargetingAudience>,
    ];
  }

  if (isTargetedRollout || isPercentageRollout) {
    const nodes = [];
    if (isTargetedRollout) {
      nodes.push(
        <TargetingAudience key="targeted" className={className} {...rest}>
          <User className="w-4 h-4" />
          <div>Targeting rules</div>
        </TargetingAudience>,
      );
    }
    if (isPercentageRollout) {
      nodes.push(
        <TargetingAudience key="percentage" className={className} {...rest}>
          <Users className="w-4 h-4" />
          <div>Percentage rollout</div>
        </TargetingAudience>,
      );
    } else {
      nodes.push(
        <TargetingAudience key="default" className={className} {...rest}>
          <Crosshair className="w-4 h-4" />
          <div>Default variant</div>
        </TargetingAudience>,
      );
    }

    return nodes;
  }

  return [
    <TargetingAudience key="default" className={className} {...rest}>
      <Crosshair className="w-4 h-4" />
      <div>Default variant</div>
    </TargetingAudience>,
  ];
};
